import { type JSX, Match, Switch } from 'solid-js';
import { t } from '../i18n';
import { isForbidden } from '../lib/api';
import { useBlockerAnswer, useBlockers } from '../lib/blockers';
import type { FleetUtilization } from '../lib/fleetUtilization';
import { useKeys } from '../lib/keys';
import { CardGridSkeleton } from './CardGridSkeleton';
import { DeniedState } from './DeniedState';
import { EmptyState } from './EmptyState';
import { ErrorState } from './ErrorState';
import { FleetMachines, type FleetMachinesProps } from './FleetMachines';
import { Panel } from './Panel';

// The Machines section's non-list states around `<FleetMachines/>` (#6560): loading, a
// denied read (no Retry), a failed read with nothing cached (retryable, #298), and an
// account with no machine yet (#6567 replaces it with the first-machine screen). Every
// state reads the one `GET /v1/fleet/utilization` query (owner ruling G3).
//
// NOT `<QueryBoundary/>`: its data arm calls its children again with every new `data`,
// which would rebuild the list on each re-read and close an open card menu. These
// arms switch on booleans that hold steady between re-reads, so the list mounts once and the
// read flows into it as a prop.
//
// THE OPEN BLOCKERS ARE THIS SECTION'S OTHER READ (#6785): one page-level
// `useBlockers(() => true)` (`read:tasks`, which every role holds), handed to the list so a
// Mission Panel card shows the ones on its current task. A failed or absent read shows none.
// Both reads are live on the account stream (#7612): the blockers re-read on the `blockers`
// topic, the machines on `machines` + `tasks`, so neither waits on the other's cadence.
//
// AND THE CARDS' ANSWER CALL (#6726): `useBlockerAnswer`, the Blockers page's `answerBlocker`
// followed by a re-read of every blockers query, built here because this section sits under
// the app's query client and the cards do not need one.

type UtilizationRead = {
  readonly isLoading: boolean;
  readonly isError: boolean;
  readonly error: unknown;
  readonly data: FleetUtilization | undefined;
  readonly refetch: () => unknown;
};

export function FleetMachinesSection(
  props: Omit<FleetMachinesProps, 'boxes' | 'blockers' | 'onAnswer' | 'keys' | 'readAt'> & {
    readonly read: UtilizationRead;
  },
): JSX.Element {
  const boxes = () => props.read.data?.boxes;
  const blockers = useBlockers(() => true, props.token);
  const onAnswer = useBlockerAnswer(props.token);
  // The cards' epic model pickers (#6753) offer the account's keys; only an admin sees one,
  // so a reader is never sent a keys read.
  const keys = useKeys(props.token, () => props.canWrite);
  return (
    <Switch>
      <Match when={props.read.isLoading}>
        <Panel class="fleet-machines" title={t('fleet.machines.heading')}>
          <div role="status">
            <span class="sr-only">{t('states.loading')}</span>
            <CardGridSkeleton listClass="machine-grid" />
          </div>
        </Panel>
      </Match>
      {/* A 403 is an answer, not a failure: no Retry (`<QueryBoundary/>`'s order). */}
      <Match when={boxes() === undefined && props.read.isError && isForbidden(props.read.error)}>
        <Panel class="fleet-machines" title={t('fleet.machines.heading')}>
          <DeniedState error={props.read.error} />
        </Panel>
      </Match>
      <Match when={boxes() === undefined && props.read.isError}>
        <Panel class="fleet-machines" title={t('fleet.machines.heading')}>
          <ErrorState error={props.read.error} onRetry={() => void props.read.refetch()} />
        </Panel>
      </Match>
      <Match when={boxes()?.length === 0}>
        <Panel class="fleet-machines" title={t('fleet.machines.heading')}>
          <EmptyState message={t('fleet.machines.empty')} />
        </Panel>
      </Match>
      <Match when={(boxes()?.length ?? 0) > 0}>
        <FleetMachines
          boxes={boxes() ?? []}
          blockers={blockers.data ?? []}
          onAnswer={onAnswer}
          view={props.view}
          onView={props.onView}
          page={props.page}
          onPage={props.onPage}
          canWrite={props.canWrite}
          onManage={props.onManage}
          onRename={props.onRename}
          onDrain={props.onDrain}
          onUndrain={props.onUndrain}
          onRemove={props.onRemove}
          notify={props.notify}
          onWritten={props.onWritten}
          token={props.token}
          send={props.send}
          keys={() => keys.data}
          readAt={() => props.read.data?.read_started_ms ?? 0}
          readOrder={() => props.read.data?.read_order ?? 0}
        />
      </Match>
    </Switch>
  );
}
